import React, { ForwardedRef, Fragment, useRef } from 'react';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import CopyToClipboard from './CopyToClipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { Dialog } from '@headlessui/react';
import { Transition } from '@headlessui/react';

export interface ShareLinkModalProps {
  shareLink: string | null;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
}

export const ShareLinkModal = (props: ShareLinkModalProps) => {
  const { shareLink, showModal, setShowModal } = props;

  const shareLinkCopilotModalRef = useRef<HTMLDivElement>(null);

  return (
    <Transition appear show={showModal} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-overlay"
        onClose={() => setShowModal(false)}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-1/2 transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-12px transition-all">
                <ShareLinkModalContents
                  shareLink={shareLink ?? ''}
                  onClose={() => setShowModal(false)}
                  ref={shareLinkCopilotModalRef}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const ShareLinkModalContents = React.forwardRef(
  (
    props: { shareLink: string; onClose: () => void },
    ref: ForwardedRef<HTMLDivElement>,
  ) => (
    <div className="ShareLinkModal" ref={ref}>
      <div className="pb-2">
        <div className="flex justify-between items-start">
          <Heading level={3} textColor={'text-black'}>
            Share with a colleague
          </Heading>
          <button onClick={props.onClose}>
            <IconCloseSvg svg={{ className: 'w-4 h-4 text-gray-600' }} />
          </button>
        </div>
      </div>
      <div className="bg-gray-100 rounded-2xl">
        <div className="px-2 py-4 flex gap-1 justify-stretch items-center">
          <Paragraph className="truncate" textColor="text-gray-1000">
            {props.shareLink}
          </Paragraph>
          <CopyToClipboard
            textToCopy={props.shareLink}
            icon={<ContentCopyIcon className="text-black w-4 h-4" />}
          />
        </div>
      </div>

      <div className="flex gap-1 justify-start items-center pt-4">
        <IconInformationSvg svg={{ className: 'w-4 h-4' }} />
        <Paragraph textColor="text-black">
          This link can only be shared with colleagues who have access to Sigma
          Squared.
        </Paragraph>
      </div>
    </div>
  ),
);

ShareLinkModalContents.displayName = 'ShareLinkModalContents';
